import cn from 'classnames'
import { observer } from 'mobx-react'
import React, { ReactElement, useState, useEffect } from 'react'
import ReCAPTCHA from 'react-google-recaptcha'

import { ClientConfigType } from '@flowwai-frontend/bps-officer-web/iam'
import { useAppTheme } from '@flowwai-frontend/bps-officer-web/style-guide'
import {
   Button,
   Hierarchy,
   SubVariant,
   TextField
} from '@flowwai-frontend/design-system'
import {
   ArrowNarrowIcon,
   User01Icon
} from '@flowwai-frontend/design-system/icons'
import { ALPHA_NUMERIC_UNDERSCORE_REGEX } from '@flowwai-frontend/shared-web/common/ui'
import { useCustomT } from '@flowwai-frontend/shared/common-components'
import {
   isLargeDesktop,
   setCookie,
   getCookie
} from '@flowwai-frontend/shared/utils/web'
import { useCaptchaGates } from '@flowwai-frontend/shared-web/feature-toggles'
import { Config } from '@flowwai-frontend/shared/constants'

import {
   loginMachineEvents,
   loginMachineTags,
   LOGIN_USERNAME_COOKIE_NAME,
   LOGIN_USERNAME_COOKIE_EXPIRY_DAYS
} from '../../constants/loginConstants'
import {
   LoginMachineSend,
   LoginMachineState
} from '../../machines/LoginMachine'

import TCAndPrivacyPolicy from './TCAndPrivacyPolicy'
import './index.css'
import * as Styles from './styles'
import PrivacyPolicy from './PrivacyPolicy'

interface LoginWithUsernameV2Props {
   loginMachineSend: LoginMachineSend

   loginMachineState: LoginMachineState
   clientConfig: ClientConfigType
}

const LoginWithUsernameV2 = (props: LoginWithUsernameV2Props): ReactElement => {
   const { loginMachineSend, loginMachineState, clientConfig } = props

   const {
      isLoginCaptchaEnabled: isCaptchaEnableInLoginPage
   } = useCaptchaGates()

   const {
      context,
      context: { username: defaultUsername }
   } = loginMachineState

   const [username, setUsername] = useState<string>(defaultUsername)

   const [isValid, setIsValid] = useState(defaultUsername ? true : false)

   const [captchaValue, setCaptchaValue] = useState<string | null>(null)

   const [errorMessage, setErrorMessage] = useState('')
   const [isTAndCAccepted, setIsTAndCAccepted] = useState(true)

   const isCaptchaSelected = !captchaValue && isCaptchaEnableInLoginPage

   const isLoginButtonDisabled =
      !isValid || !isTAndCAccepted || isCaptchaSelected

   // Load saved username from cookie on component mount
   useEffect(() => {
      const savedUsername = getCookie(LOGIN_USERNAME_COOKIE_NAME)
      if (savedUsername) {
         setUsername(savedUsername)
         validate(savedUsername)
      }
   }, [])

   const customT = useCustomT('auth:login')
   const authErrorT = useCustomT('auth:errorMessages')

   const { theme } = useAppTheme()
   const { loginWithEmail: loginCardTheme } = theme.auth.login

   const errorTransitionClassName = 'login-textfield-error-transition '

   const isLargeDevice = isLargeDesktop()

   useEffect(() => {
      if (username) {
         validate(username)
      }
   }, [username])

   const onBlurUsername = (): void => {
      validate(username || '')
   }

   const unsetError = (): void => {
      setIsValid(true)
      setError('')
   }

   const setError = (errorText: string): void => {
      setErrorMessage(errorText)
   }

   const validateUsername = (value: string) => {
      const isValid = ALPHA_NUMERIC_UNDERSCORE_REGEX.test(value)
      return isValid
   }

   const validate = (value: string): void => {
      if (!value) {
         setError(authErrorT('required'))
         setIsValid(false)
         return
      }
      const isValidUsername = validateUsername(value)

      if (isValidUsername) {
         unsetError()
         setIsValid(true)
      } else {
         setError(authErrorT('invalidUsername'))
         setIsValid(false)
      }
   }

   const onChangeUsername = (value: string): void => {
      setUsername(value)

      validate(value)
      loginMachineSend({
         type: loginMachineEvents.onChangeValue
      })
   }

   const onClickSendOTP = (event: React.SyntheticEvent): void => {
      event.preventDefault()
      if (isValid) {
         if (!username) return

         // Save username to cookie when Get OTP button is clicked
         setCookie(
            LOGIN_USERNAME_COOKIE_NAME,
            username,
            LOGIN_USERNAME_COOKIE_EXPIRY_DAYS
         ) // 1 year expiry

         loginMachineSend({
            type: loginMachineEvents.requestUsernameOtp,
            username: username,
            isAcceptedTnc: isTAndCAccepted,
            captchaToken: isCaptchaEnableInLoginPage ? captchaValue : undefined
         })
      }
   }

   const renderLeftIcon = (): ReactElement => (
      <div className='mr-8px'>
         <User01Icon height={18} width={18} fill={loginCardTheme.iconColor} />
      </div>
   )

   const renderHeader = (): ReactElement => (
      <>
         <span
            className={cn(
               Styles.loginTextStyles,
               loginCardTheme.loginTextColor
            )}
         >
            {customT('officerPortalLoginText')}
         </span>
         <span
            className={cn(
               Styles.loginDescriptionStyles,
               loginCardTheme.loginDescriptionTextColor
            )}
         >
            {customT('officerPortalLoginUsernameDescription')}
         </span>
      </>
   )

   const onCaptchaValueChange = (value: string | null): void => {
      setCaptchaValue(value)
   }

   return (
      <form
         className={cn(
            Styles.getLoginContainerStyles(isLargeDevice),
            loginCardTheme.defaultBorderColor,
            loginCardTheme.cardBgColor
         )}
         onSubmit={onClickSendOTP}
      >
         {renderHeader()}

         <TextField
            size='Small'
            onBlur={onBlurUsername}
            errorMessage={errorMessage || context.errorMessage}
            value={username || ''}
            type='text'
            onChange={onChangeUsername}
            label={customT('officerPortalUsernameLabel')}
            placeholder={customT('officerPortalUsernamePlaceholder')}
            leftElement={renderLeftIcon}
            errorTransitionClassName={errorTransitionClassName}
            autoFocus
            autoComplete='username'
            name='username'
         />
         {/* <TCAndPrivacyPolicy
            privacyPolicyUrl={clientConfig.privacyPolicyAndTC.privacyPolicyUrl}
            termsAndConditionsUrl={
               clientConfig.privacyPolicyAndTC.termsAndConditionsUrl
            }
            isTAndCAccepted={isTAndCAccepted}
            setIsTAndCAccepted={setIsTAndCAccepted}
            className='mt-6'
         /> */}

         {isCaptchaEnableInLoginPage ? (
            <div className={cn(Styles.captchaContainer)}>
               <ReCAPTCHA
                  sitekey={Config.CAPTCHA_SITE_KEY}
                  onChange={onCaptchaValueChange}
               />
            </div>
         ) : null}

         <Button
            type='submit'
            hierarchy={Hierarchy.Primary}
            subVariant={SubVariant.Primary}
            size={'Large'}
            isDisabled={isLoginButtonDisabled}
            rightIcon={() => <ArrowNarrowIcon />}
            className='mt-6'
            isLoading={loginMachineState.hasTag(
               loginMachineTags.requestOtpLoading
            )}
         >
            {customT('getOtp')}
         </Button>
         <PrivacyPolicy
            privacyPolicyUrl={clientConfig.privacyPolicyAndTC.privacyPolicyUrl}
            className='mt-4'
         />
      </form>
   )
}

export default observer(LoginWithUsernameV2)
